/**
 * Get LaunchDarkly user context from localStorage.
 * Only returns context when an Okta session exists (merchant flow).
 *
 * The context key is the Okta `preferred_username`. All values from the
 * `CURRENT_USER` localStorage entry are added as custom attributes:
 *   - User-level fields (identical across profiles) are set as scalars.
 *   - Merchant-level fields are aggregated across all linked profiles into
 *     de-duplicated arrays (consistent with the existing `merchantNames`).
 *   - `features` booleans are OR-aggregated: a feature is enabled if ANY
 *     linked profile enables it.
 */
export const getLaunchDarklyUserContext = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    // Only initialise LD context for the merchant flow (Okta session present)
    const oktaTokenStorage = localStorage.getItem('okta-token-storage');
    if (!oktaTokenStorage) {
      return null;
    }

    const oktaData = JSON.parse(oktaTokenStorage);
    const userId = oktaData?.idToken?.claims?.preferred_username || null;

    if (!userId) {
      return null;
    }

    const custom = {};
    const currentUser = localStorage.getItem('CURRENT_USER');
    if (currentUser) {
      const userData = JSON.parse(currentUser);

      if (userData?.mode) {
        custom.mode = userData.mode;
      }

      const profiles = Array.isArray(userData?.profiles)
        ? userData.profiles
        : [];

      if (profiles.length > 0) {
        // User-level attributes (identical across profiles) — take the first
        const [firstProfile] = profiles;
        ['userEmail', 'userGivenName', 'firstName', 'lastName', 'isTrainingRequired'].forEach(
          (attr) => {
            if (firstProfile[attr] !== undefined) {
              custom[attr] = firstProfile[attr];
            }
          }
        );

        // Merchant-level attributes — aggregate (de-duplicated) across profiles
        const collect = (attr) => [
          ...new Set(
            profiles
              .map((profile) => profile[attr])
              .filter((value) => value !== undefined && value !== null)
          ),
        ];

        custom.merchantIds = collect('merchantId');
        custom.merchantNames = collect('merchantName');
        custom.currencies = collect('currency');
        custom.countries = collect('country');
        custom.paymentMethods = collect('paymentMethod');
        custom.logoURLs = collect('logoURL');
        custom.isHopSop = collect('isHopSop').includes(true);

        // Union of all user claims across profiles
        custom.userClaims = [
          ...new Set(
            profiles.flatMap((profile) =>
              Array.isArray(profile.userClaims) ? profile.userClaims : []
            )
          ),
        ];

        // Feature flags — enabled if ANY linked profile enables it
        profiles.forEach((profile) => {
          const features = profile.features || {};
          Object.keys(features).forEach((featureKey) => {
            custom[featureKey] =
              Boolean(custom[featureKey]) || Boolean(features[featureKey]);
          });
        });
      }
    }

    return {
      key: userId,
      ...(Object.keys(custom).length > 0 && { custom }),
    };
  } catch {
    return null;
  }
};
